/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Checkbox,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import parentPaths from '../../../services/paths';
import { copyText } from '../../../services/helpers/utils';

import DeletePopup from '../../../components/Popup/DeletePopup';

import './productFile.scss';
import { VARIABLE_TYPE } from '../../../components/utils/Modals/EditVariationModal/constants';

const useStyles = makeStyles({
  button: {
    color: '#303030',
    '&:hover': {
      color: '#4791db',
    },
    '&:active': {
      color: '#4791db',
    },
  },
});

const ProductVariationsTable = ({
  currentProductData,
  handleDeleteVariation,
  productVariations,
  setSortedBundledProducts,
  history,
  productId,
  sortedBundledProducts = [],
  productDetails,
}) => {
  const [orderBy, setOrderBy] = useState('updateLast');
  const [order, setOrder] = useState('desc');
  const [selectedItems, setSelectedItems] = useState([]);

  const classes = useStyles();

  const isSelectedAll = selectedItems.length === sortedBundledProducts?.length;

  const onRequestSort = (event, property) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);

    const compareFn = (a, b) => (order === 'asc'
      ? (`${a[property]}`).localeCompare(b[property]) : (`${b[property]}`).localeCompare(a[property]));

    setSortedBundledProducts(productVariations?.bundledProducts.sort(compareFn));
  };

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const handleCheckAll = () => {
    if (isSelectedAll) {
      setSelectedItems([]);

      return;
    }

    setSelectedItems(sortedBundledProducts.map(({ id }) => id));
  };

  return (
    <Box px={2}>
      <Typography>Emphasized values override parent product's values.</Typography>
      <Box mt={3}>
        <Box mb={3} display="flex" justifyContent="space-between">
          <Button
            variant='outlined'
            data-test='addVariant'
            color='primary'
            onClick={() => {
              history.push(`${parentPaths.productlist}/add`, {
                parentId: productId,
                newVariation: 'newVariation',
              });
            }}
          // disabled={!currentProductData?.availableVariables?.length}
          >
            Add variant
          </Button>
          <IconButton
            disabled={selectedItems.length === 0}
            className={classes.button}
            edge="start"
            color="secondary"
            size="large"
          >
            <DeletePopup
              selectedItems={selectedItems}
              checkedItem={selectedItems.length > 1}
              deleteFunc={handleDeleteVariation}
            />
          </IconButton>
        </Box>
        <TableContainer component={Paper}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell align='center' sortDirection='asc'>
                  <TableSortLabel
                    active={orderBy === 'updateDate'}
                    direction={order}
                    onClick={createSortHandler('updateDate')}
                    hideSortIcon
                  >
                    Last Update
                  </TableSortLabel>
                </TableCell>
                <TableCell align='center' sortDirection='asc'>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={order}
                    onClick={createSortHandler('status')}
                    hideSortIcon
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align='center' sortDirection='asc'>
                  <TableSortLabel
                    active={orderBy === 'publisherRefId'}
                    direction={order}
                    onClick={createSortHandler('publisherRefId')}
                    hideSortIcon
                  >
                    Publisher reference
                  </TableSortLabel>
                </TableCell>
                <TableCell align='center' sortDirection='asc'>
                  <TableSortLabel
                    active={orderBy === 'lifeTime'}
                    direction={order}
                    onClick={createSortHandler('lifeTime')}
                    hideSortIcon
                  >
                    Lifetime
                  </TableSortLabel>
                </TableCell>
                <TableCell align='center' sortDirection='asc'>
                  <TableSortLabel
                    active={orderBy === 'fulfillmentTemplate'}
                    direction={order}
                    onClick={createSortHandler('fulfillmentTemplate')}
                    hideSortIcon
                  >
                    Fulfillment Model
                  </TableSortLabel>
                </TableCell>
                <TableCell align='center' sortDirection='asc'>
                  <TableSortLabel
                    active={orderBy === 'subscriptionTemplate'}
                    direction={order}
                    onClick={createSortHandler('subscriptionTemplate')}
                    hideSortIcon
                  >
                    Subscription Model
                  </TableSortLabel>
                </TableCell>

                {currentProductData?.availableVariables
                  && currentProductData?.availableVariables.map(({ field = ''}) => (
                    <TableCell key={field} align='center' sortDirection='asc'>
                      <TableSortLabel
                        active={orderBy === field}
                        direction={order}
                        onClick={createSortHandler(field)}
                        hideSortIcon
                      >
                        {field}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                <TableCell align='center' width='75px' >
                  <Box display="flex" flexDirection="column">
                    <Checkbox
                      checked={isSelectedAll}
                      onChange={handleCheckAll}
                      name="deleteVariants"
                      color="primary"
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-test='productVariants'>
              {sortedBundledProducts?.map((item) => {
                const {
                  id,
                  updateDate,
                  status,
                  publisherRefId,
                  lifeTime,
                  fulfillmentTemplate,
                  fulfillmentTemplateName,
                  subscriptionTemplate,
                  ...params
                } = item;
                const curFulfillment = fulfillmentTemplateName || fulfillmentTemplate === '' ? fulfillmentTemplateName || fulfillmentTemplate : currentProductData?.fulfillmentTemplate ? currentProductData?.fulfillmentTemplateName : '';
                const curSubscriptionTemplate = subscriptionTemplate || subscriptionTemplate === '' ? subscriptionTemplate : currentProductData?.subscriptionTemplate || '';

                const getItemParamValue = (variant) => {
                  const isDefault = !params[variant?.field]
                    || (params[variant?.field] === variant?.defaultValue);
                  const varValue = isDefault ? variant?.defaultValue : params[variant?.field];
                  let varLabel = variant?.defaultValue;

                  const curValValueDescr = productDetails?.variableDescriptions
                    .find((d) => d.description === variant?.field)
                    ?.variableValueDescriptions
                    ?.find((d) => d.description === varValue);

                  if (curValValueDescr) {
                    const availDescriptions = Object.entries(curValValueDescr?.localizedValue)
                      .filter(([, v]) => !!v) || [];

                    if (availDescriptions?.length) {
                      const hasEnDescr = availDescriptions.some((v) => v.includes('en-US'));
                      const [firstLang] = availDescriptions[0];

                      varLabel = curValValueDescr?.localizedValue[hasEnDescr ? 'en-US' : firstLang] || curValValueDescr?.descValue;
                    }
                  }

                  if (variant?.type === VARIABLE_TYPE.RANGE) {
                    varLabel = `${varValue} ${varLabel}`;
                  }

                  return {
                    isDefault,
                    varLabel,
                  };
                };

                return (
                  <TableRow
                    key={id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      history.push(`${parentPaths.productlist}/${id}`, {
                        parentId: productId,
                      });
                    }}
                  >
                    <TableCell component='th' scope='row'>
                      <ContentCopyIcon
                        onClick={(e) => { e.stopPropagation(); copyText(id); }}
                        color="secondary"
                        style={{
                          marginRight: '5px', fontSize: '16px', position: 'relative', top: '2px',
                        }}
                        className="copyIcon"
                      />
                      <Link to={`${parentPaths.productlist}/${id}`} className='link-to-variation'>{id}</Link>
                    </TableCell>
                    <TableCell
                      align='center'
                    >
                      {moment(updateDate).format('D MMM YYYY HH:mm:ss') || ''}
                    </TableCell>
                    <TableCell
                      align='center'
                      className={`status-${status === 'ENABLED' ? 'enabled' : 'disabled'}`}
                    >
                      {status || ''}
                    </TableCell>
                    <TableCell
                      align='center'
                      style={{ color: currentProductData.publisherRefId !== publisherRefId && '#719ded' }}
                    >
                      {publisherRefId || '-'}
                    </TableCell>
                    <TableCell
                      align='center'
                      style={{ color: currentProductData.lifeTime !== lifeTime && '#719ded' }}
                    >
                      {lifeTime || ''}
                    </TableCell>
                    <TableCell
                      align='center'
                      style={fulfillmentTemplateName && fulfillmentTemplate ? { color: '#719ded' } : { color: '#b9b1b1' }}
                    >
                      {curFulfillment}
                    </TableCell>
                    <TableCell
                      align='center'
                      style={subscriptionTemplate ? { color: '#719ded' } : { color: '#b9b1b1' }}
                    >
                      {curSubscriptionTemplate}
                    </TableCell>

                    {currentProductData?.availableVariables
                      && currentProductData?.availableVariables.map((v) => {
                        const { isDefault, varLabel } = getItemParamValue(v);

                        return <TableCell key={varLabel} align="center" style={{ color: isDefault ? '' : '#719ded' }}>{varLabel}</TableCell>;
                      })}

                    <TableCell align="center">
                      <Box onClick={(e) => { e.stopPropagation(); }}>
                        <Checkbox
                          checked={selectedItems.includes(id)}
                          onChange={() => {
                            setSelectedItems((prev) => {
                              if (prev.includes(id)) {
                                return prev.filter((selectedId) => selectedId !== id);
                              }

                              return [...prev, id];
                            });
                          }}
                          name="deleteVariant"
                          color="primary"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

ProductVariationsTable.propTypes = {
  setSortedBundledProducts: PropTypes.func,
  currentProductData: PropTypes.object,
  productVariations: PropTypes.shape({
    bundledProducts: PropTypes.array,
    variations: PropTypes.object,
  }),
  handleDeleteVariation: PropTypes.func,
  history: PropTypes.object,
  productId: PropTypes.string,
  sortedBundledProducts: PropTypes.array,
  productDetails: PropTypes.object,
};

export default ProductVariationsTable;
